/**
 * LocalPageTemplate - Renders pages from local YAML files
 *
 * This template handles pages stored in content/pages/*.yaml
 * It transforms the agent-first YAML structure into existing components.
 */

import { graphql } from "gatsby"
import React from "react"
import HubspotFormEmbed from "components/HubspotFormEmbed"
import PartnerContactForm from "components/PartnerContactForm"
import { SEOHead } from "components/SEO"
import {
  generateLocalPageGraphSchema,
  generateCollectionPageGraphSchema,
} from "utils/structuredData"
import {
  transformHeroToComponent,
  transformDarkHeroToComponent,
  transformFeatureGridToComponent,
  transformCardsToComponent,
  transformFaqToComponent,
  transformCtaToComponent,
  transformExpertCtaToComponent,
  transformMarkdownToComponent,
  transformLogoGridToComponent,
  transformTwoCardsToComponent,
  transformImageTextToComponent,
  transformPricingToComponent,
  transformContactFormToComponent,
  transformRichTextToComponent,
  transformHubspotFormToComponent,
  transformBulletGridToComponent,
  transformDemoToComponent,
  transformSupportToComponent,
  transformComparisonTableToComponent,
  markdownToRichText,
} from "effects/usePageContent"
import ApacheSupersetExpertSection from "components/Blocks/ApacheSupersetExpertSection"
import Column2BulletGrid from "components/Blocks/Column2BulletGrid"
import Column2CardGroup from "components/Blocks/Column2CardGroup"
import Column3CardGroup from "components/Blocks/Column3CardGroup"
import BoothTeam from "components/Blocks/BoothTeam"
import SocialLinks from "components/Blocks/SocialLinks"
import ComparisonTable from "components/Blocks/ComparisonTable"
import MultiComparisonTable from "components/Blocks/MultiComparisonTable"
import ContactSection from "components/Blocks/ContactSection"
import EmbedFormSection from "components/Blocks/EmbedFormSection"
import CTASection from "components/Blocks/CTASection"
import CustomersSection from "components/Blocks/CustomersSection"
import DashboardLogo from "components/Blocks/DashboardLogo"
import DemoSection from "components/Blocks/DemoSection"
import DeploymentCards from "components/Blocks/DeploymentCards"
import FAQ from "components/Blocks/FAQ"
import FeatureSection from "components/Blocks/FeatureSection"
import GatedWhitepaperCTA from "components/GatedWhitepaperCTA"
import HeroSection from "components/Blocks/HeroSection"
import JoinSection from "components/Blocks/JoinSection"
import PageMarkdown from "components/Blocks/PageMarkdown"
import PageRichText from "components/Blocks/PageRichText"
import PartnersSection from "components/Blocks/PartnersSection"
import PillGrid from "components/Blocks/PillGrid"
import PricingSection from "components/Blocks/PricingSection"
import ProofCards from "components/Blocks/ProofCards"
import StatCallout from "components/Blocks/StatCallout"
import SupportSection from "components/Blocks/SupportSection"
import TwoSideImageAndText from "components/Blocks/TwoSideImageAndText"
import VsCards from "components/Blocks/VsCards"
import Layout from "components/ui/Layout"
import LogoTicker from "components/pages/home/LogoTicker"
import RelatedBlogPosts from "components/Blocks/RelatedBlogPosts"
import SkewedSection from "components/ui/Layout/SkewedSection"

function LocalPage({ data, location: { pathname } }) {
  const page = data.pagesYaml

  // Determine hero type and transform appropriately
  // Use headerDark as fallback since hero.type is a nested "type" field
  // that can occasionally be tricky for GraphQL schema inference
  const isDarkHero =
    page.hero?.type === "dark" || (!!page.hero && page.headerDark)
  const heroProps = isDarkHero
    ? transformDarkHeroToComponent(page.hero)
    : transformHeroToComponent(page.hero)

  // Render sections based on their type
  const renderSection = (section, index) => {
    switch (section.type) {
      case "feature-grid":
        return (
          <Column2BulletGrid
            key={`section-${index}`}
            {...transformFeatureGridToComponent(section)}
          />
        )
      case "cards":
        return (
          <Column2CardGroup
            key={`section-${index}`}
            {...transformCardsToComponent(section)}
          />
        )
      case "faq":
        return (
          <FAQ key={`section-${index}`} {...transformFaqToComponent(section)} />
        )
      case "cta":
        return (
          <CTASection
            key={`section-${index}`}
            {...transformCtaToComponent(section)}
          />
        )
      case "cta-form":
        return (
          <div key={`section-${index}`} id="contact">
            <CTASection
              {...transformCtaToComponent(section)}
              splitLayout
              noBackground={section.noBackground}
            >
              <PartnerContactForm
                formId={section.formId}
                trackSubmit={section.trackSubmit}
                thankYouMessage={section.thankYouMessage}
                sourcePage={page.slug}
              />
            </CTASection>
          </div>
        )
      case "expert-cta":
        return (
          <ApacheSupersetExpertSection
            key={`section-${index}`}
            {...transformExpertCtaToComponent(section)}
          />
        )
      case "markdown":
        return (
          <PageMarkdown
            key={`section-${index}`}
            {...transformMarkdownToComponent(section)}
          />
        )
      case "logo-grid":
        return (
          <DashboardLogo
            key={`section-${index}`}
            {...transformLogoGridToComponent(section)}
          />
        )
      case "logo-ticker":
        return <LogoTicker key={`section-${index}`} />
      case "two-cards":
        return (
          <JoinSection
            key={`section-${index}`}
            {...transformTwoCardsToComponent(section)}
          />
        )
      case "image-text":
        return (
          <TwoSideImageAndText
            key={`section-${index}`}
            {...transformImageTextToComponent(section)}
          />
        )
      case "pricing":
        return (
          <PricingSection
            key={`section-${index}`}
            {...transformPricingToComponent(section)}
          />
        )
      case "contact-form":
        return (
          <ContactSection
            key={`section-${index}`}
            {...transformContactFormToComponent(section)}
          />
        )
      case "newsletter-form":
      case "gated-download-form":
        return (
          <EmbedFormSection
            key={`section-${index}`}
            {...transformContactFormToComponent(section)}
          />
        )
      case "rich-text":
        return (
          <PageRichText
            key={`section-${index}`}
            {...transformRichTextToComponent(section)}
          />
        )
      case "hubspot-form":
        return (
          <div key={`section-${index}`} className="container py-10">
            <HubspotFormEmbed
              {...transformHubspotFormToComponent(section, {
                sourcePage: page.slug,
              })}
            />
          </div>
        )
      case "bullet-grid":
        if (section.skewed) {
          return (
            <SkewedSection key={`section-${index}`}>
              <Column2BulletGrid {...transformBulletGridToComponent(section)} />
            </SkewedSection>
          )
        }
        return (
          <Column2BulletGrid
            key={`section-${index}`}
            {...transformBulletGridToComponent(section)}
          />
        )
      case "customers":
        return (
          <CustomersSection
            key={`section-${index}`}
            useLocalData={section.useLocalData}
          />
        )
      case "partners":
        return (
          <PartnersSection
            key={`section-${index}`}
            showAllCategories={section.showAllCategories}
          />
        )
      case "demo":
        return (
          <DemoSection
            key={`section-${index}`}
            {...transformDemoToComponent(section)}
          />
        )
      case "support":
        return (
          <SupportSection
            key={`section-${index}`}
            {...transformSupportToComponent(section)}
          />
        )
      case "comparison-table":
        return (
          <ComparisonTable
            key={`section-${index}`}
            {...transformComparisonTableToComponent(section)}
          />
        )
      case "multi-comparison-table":
        return (
          <MultiComparisonTable
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            competitors={section.competitors}
            rows={section.multiRows}
          />
        )
      case "social-links":
        return (
          <SocialLinks
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            networks={section.networks}
          />
        )
      case "team-cards":
        return (
          <BoothTeam
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            people={section.people}
          />
        )
      case "feature-section":
        return (
          <FeatureSection
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            supportItems={(section.items || []).map((item) => ({
              description: item.heading,
              image: item.icon ? { url: item.icon } : null,
            }))}
          />
        )
      case "column3-cards":
        return (
          <Column3CardGroup
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            subHeading={section.subheading}
            boxItems={(section.cards || []).map((card, cardIndex) => ({
              id: `card-${cardIndex}`,
              heading: card.heading,
              richText: markdownToRichText(card.description),
              logo: card.logo ? { url: card.logo } : null,
              link: card.link,
              ctaLink: card.button?.url,
              ctaButton: card.button
                ? {
                    buttonLabel: card.button.text,
                    url: card.button.url,
                  }
                : null,
            }))}
          />
        )
      case "blog-cards":
        return (
          <RelatedBlogPosts
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            subHeading={section.subheading}
            blogSlugs={section.blogs || []}
          />
        )
      case "stat-callout":
        return (
          <StatCallout
            key={`section-${index}`}
            stat={section.stat}
            statLabel={section.statLabel}
            description={section.description}
            descriptionHtml={section.descriptionHtml}
          />
        )
      case "vs-cards":
        return (
          <VsCards
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            competitorLabel={section.competitorLabel}
            cards={section.cards || []}
          />
        )
      case "pill-grid":
        return (
          <PillGrid
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            descriptionHtml={section.descriptionHtml}
            pills={section.pills || []}
          />
        )
      case "deployment-cards":
        return (
          <DeploymentCards
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            descriptionHtml={section.descriptionHtml}
            cards={section.cards || []}
          />
        )
      case "proof-cards":
        return (
          <ProofCards
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            descriptionHtml={section.descriptionHtml}
            cards={section.cards || []}
            footer={section.footer}
            footerHtml={section.footerHtml}
          />
        )
      case "gated-whitepaper-cta":
        return (
          <GatedWhitepaperCTA
            key={`section-${index}`}
            label={section.label}
            heading={section.heading}
            description={section.description}
            buttonText={section.button?.text || "Download Whitepaper"}
            pdfUrl={section.pdfUrl}
            sourcePage={`${page.slug}-whitepaper`}
            trackingName={section.button?.trackingName || "Download Whitepaper"}
          />
        )
      // TODO: Implement these section types
      case "dark":
        // Theme modifier - currently no-op, would need to wrap subsequent sections
        return null
      case "upcoming-events":
        // Would display upcoming events from events data
        // For now, skip this section
        return null
      default:
        console.warn(`Unknown section type: ${section.type}`)
        return null
    }
  }

  // Only use floating header when there's a hero section to accommodate the overlap
  const hasHero = !!heroProps

  return (
    <Layout
      headerFloating={hasHero}
      headerDark={page.headerDark}
      trackingName={page.trackingName}
      pageName={page.slug}
      pagePath={pathname}
    >
      {/* Hero Section - different component based on hero type */}
      {heroProps && isDarkHero && <HeroSection {...heroProps} />}
      {heroProps && !isDarkHero && <TwoSideImageAndText {...heroProps} />}

      {/* Content Sections */}
      {page.sections?.map((section, index) => renderSection(section, index))}
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head({ data, location }) {
  const page = data.pagesYaml

  // Build meta array for robots if specified
  const meta = []
  if (page.robotsMeta && page.robotsMeta.length > 0) {
    meta.push({
      name: "robots",
      content: page.robotsMeta.join(", "),
    })
  }

  const siteUrl = "https://preset.io"
  const pageUrl = `${siteUrl}${location.pathname}`

  const graphSchema = page.schemaCollection
    ? generateCollectionPageGraphSchema({
        pageUrl,
        pageTitle: page.title,
        pageDescription: page.schemaCollection.pageDescription,
        hasPart: page.schemaCollection.hasPart || [],
        softwareDescription:
          page.schemaCollection.softwareDescription ?? undefined,
        softwareFeatureList: page.schemaCollection.softwareFeatureList || [],
      })
    : generateLocalPageGraphSchema({
        pageUrl,
        pageTitle: page.title,
        faqs: page.faqs || [],
        schemaProduct: page.schemaProduct || null,
      })

  return (
    <SEOHead
      title={page.title}
      description={page.description}
      thumbnail={page.thumbnail}
      pageUrl={pageUrl}
      meta={meta}
      structuredData={[graphSchema]}
    />
  )
}

export const query = graphql`
  query LocalPageQuery($slug: String!) {
    pagesYaml(slug: { eq: $slug }) {
      title
      slug
      description
      thumbnail
      trackingName
      headerDark
      robotsMeta
      faqs {
        question
        answer
      }
      schemaProduct {
        name
        alternateName
        description
        featureList
        offerUrl
        offerCategory
        offerDescription
      }
      schemaCollection {
        pageDescription
        hasPart {
          name
          url
        }
        softwareDescription
        softwareFeatureList
      }
      hero {
        type
        label
        heading
        description
        video
        carousel {
          src
          caption
        }
        image {
          url
          alt
        }
        imageLink
        imageSize
        decorated
        background {
          url
          alt
        }
        animatedBackground
        buttons {
          text
          url
          color
          trackingName
          contactModal
          contactModalSource
        }
        formId
        thankYouMessage
        fullHeight
      }
      sections {
        type
        layout
        compact
        presetColumn
        competitorColumn
        rows {
          feature
          preset
          presetNote
          competitor
          competitorNote
        }
        competitors {
          name
          url
        }
        multiRows {
          feature
          preset
          presetNote
          competitors
          competitorNotes
        }
        footerNote
        label
        heading
        subheading
        description
        descriptionHtml
        content
        contentHtml
        useLocalData
        showAllCategories
        features {
          heading
          description
          icon
        }
        cards {
          heading
          description
          descriptionHtml
          logo
          document
          link
          linkText
          presetLabel
          presetText
          presetTextHtml
          competitorLabel
          competitorText
          competitorTextHtml
          badge
          highlighted
          category
        }
        categories {
          title
          questions {
            question
            answer
            answerHtml
          }
        }
        button {
          text
          url
          trackingName
          contactModal
          contactModalSource
        }
        buttons {
          text
          url
          color
          trackingName
          contactModal
          contactModalSource
        }
        primaryButton {
          text
          url
          trackingName
        }
        secondaryButton {
          text
          url
          trackingName
        }
        bottomText
        image {
          url
          alt
        }
        imageSize
        imageLink
        imageShadow
        carousel {
          src
          caption
        }
        badge {
          image
          url
          alt
        }
        logos {
          url
          alt
        }
        leftCard {
          text
          logo
          button {
            text
            url
            trackingName
          }
        }
        centerCard {
          text
          logo
          button {
            text
            url
            trackingName
          }
        }
        rightCard {
          text
          logo
          button {
            text
            url
            trackingName
          }
        }
        centered
        tiers {
          name
          icon
          description
          price
          priceUnit
          priceSubtext
          highlighted
          badge
          featuresHeading
          features
          optionsHeading
          options
          addOnsHeading
          addOns
          aiHighlight {
            heading
            items
          }
          button {
            text
            url
            trackingName
            contactModal
            contactModalSource
          }
        }
        hubspotFormId
        formId
        formVariant
        formWidth
        width
        hiddenFields
        trackSubmit
        thankYouMessage
        requireJobTitle
        sidebarHeading
        sidebarText
        sidebarContactHeading
        sidebarContactText
        successMessage
        logosHeading
        headingImage {
          url
          alt
        }
        htmlContent
        bullets {
          heading
          description
          icon
          link
        }
        videoUrl
        recordedVideoDate
        handsOnLab {
          label
          title
          description
          webinarTopic
        }
        footer
        footerBackground
        items {
          heading
          description
          icon
          ctaText
          ctaLink
        }
        supportFormUrl
        backgroundImage
        background {
          url
          alt
        }
        inverted
        flipped
        skewed
        noBackground
        pdfUrl
        people {
          name
          title
          photo
          linkedin
          meetingUrl
          meetingLabel
        }
        networks
        stat
        statLabel
        pills
        competitorLabel
        footerHtml
      }
    }
  }
`

export default LocalPage
